class DashboardCheckinStreak extends HTMLElement {
  connectedCallback() {
    this._config = this._readConfig();
    this._form = this.querySelector('#checkin-form');
    this._msg = this.querySelector('#checkin-message');
    this._grid = this.querySelector('#checkin-days-grid');
    this._monthLabel = this.querySelector('#checkin-month-label');
    this._button = this._form ? this._form.querySelector('button[type="submit"], a') : null;
    this._todayIndex = -1;
    this._todayDate = null;

    this._fetchStatus();

    if (this._form) {
      this._form.addEventListener('submit', (e) => this._onSubmit(e));
    }
  }

  _readConfig() {
    const script = this.querySelector('[data-config-json]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return {};
    }
  }

  _dayCellHtml(dateStr, state) {
    const date = new Date(dateStr);
    const dayNumber = date.getDate();
    const weekdayLabel = (this._config.weekdayShort || {})[String(date.getDay())] || '';

    if (state === 'done') {
      return `
        <div class="flex flex-col items-center gap-1">
          <div class="w-8 h-8 rounded-full bg-accent text-primary flex items-center justify-center body-xxs font-bold">${this._config.checkIconSvg || ''}</div>
          <span class="body-xxs text-text-muted">${weekdayLabel}</span>
        </div>
      `;
    }
    if (state === 'today') {
      return `
        <div class="flex flex-col items-center gap-1">
          <div class="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center body-xxs font-bold">${dayNumber}</div>
          <span class="body-xxs text-primary font-semibold">${weekdayLabel}</span>
        </div>
      `;
    }
    return `
      <div class="flex flex-col items-center gap-1">
        <div class="w-8 h-8 rounded-full bg-surface border border-border text-text-muted flex items-center justify-center body-xxs">${dayNumber}</div>
        <span class="body-xxs text-text-muted">${weekdayLabel}</span>
      </div>
    `;
  }

  _renderGrid(days, today) {
    if (!this._grid || !days || !days.length) return;
    this._todayIndex = days.findIndex((d) => d.date === today);
    this._todayDate = today;

    this._grid.innerHTML = days
      .map((day, index) => {
        const state = day.checked_in ? 'done' : index === this._todayIndex ? 'today' : 'future';
        return this._dayCellHtml(day.date, state);
      })
      .join('');

    this._renderMonthLabel(days);
  }

  _renderMonthLabel(days) {
    if (!this._monthLabel || !days.length) return;
    const monthNames = this._config.monthNames || [];
    const firstMonth = monthNames[new Date(days[0].date).getMonth()] || '';
    const lastMonth = monthNames[new Date(days[days.length - 1].date).getMonth()] || '';

    this._monthLabel.textContent =
      firstMonth === lastMonth
        ? firstMonth
        : (this._config.monthRangeTemplate || '%start_month% - %end_month%')
            .replace('%start_month%', firstMonth)
            .replace('%end_month%', lastMonth);
  }

  _setStreak(streak) {
    if (streak === null || streak === undefined) return;
    const el = document.getElementById('checkin-streak-count');
    if (el) el.textContent = streak;
  }

  _fetchStatus() {
    const endpoint = this.dataset.endpoint || '/apps/lensza/checkin-status';

    fetch(endpoint)
      .then((r) => {
        if (!r.ok) throw new Error(`checkin-status failed: ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (!data || !data.days || !data.days.length) return;
        this._renderGrid(data.days, data.today);
        this._setStreak(data.current_streak);

        const todayEntry = data.days[this._todayIndex];
        if (todayEntry && todayEntry.checked_in) {
          this._disableForm(this._config.alreadyCheckedIn);
        }
      })
      .catch(() => {
        // Fetch failed — keep the static SSR fallback grid as-is.
      })
      .finally(() => this._stopLoading());
  }

  // Drops the skeletons once the request settles, success or failure, so a failed fetch
  // rests on the placeholder grid instead of shimmering forever. The streak badge sits in
  // the card header outside this component, hence the by-id lookup.
  _stopLoading() {
    [this, document.getElementById('checkin-streak-badge')].forEach((el) => {
      if (!el) return;
      el.removeAttribute('data-loading');
      el.removeAttribute('aria-busy');
    });
  }

  _disableForm(message) {
    if (this._button) this._button.setAttribute('disabled', 'true');
    if (this._msg) this._msg.textContent = message || '';
  }

  _markTodayDone() {
    if (!this._grid || this._todayIndex < 0 || !this._todayDate) return;
    const cell = this._grid.children[this._todayIndex];
    if (cell) cell.outerHTML = this._dayCellHtml(this._todayDate, 'done');
  }

  _onSubmit(event) {
    event.preventDefault();
    const url = this.dataset.checkinUrl || '/apps/lensza/checkin';

    if (this._button) this._button.setAttribute('disabled', 'true');
    if (this._msg) this._msg.textContent = this._config.processing || '';

    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then((r) => r.json())
      .then((data) => {
        if (!data || data.error) throw new Error((data && data.error) || 'checkin_failed');

        if (typeof data.streak === 'number') this._setStreak(data.streak);
        this._markTodayDone();

        if (this._msg) {
          this._msg.textContent = data.already_done
            ? this._config.alreadyCheckedIn || ''
            : this._config.successMessage || '';
          this._msg.style.color = data.already_done ? '' : 'var(--color-success)';
        }

        // New points were credited — let other dashboard cards (welcome hero,
        // points wallet) know so they can refetch /apps/lensza/dashboard-summary.
        if (!data.already_done) {
          document.dispatchEvent(new CustomEvent('lensza:points-updated', { detail: data }));
        }
      })
      .catch(() => {
        if (this._msg) {
          this._msg.textContent = this._config.errorMessage || '';
          this._msg.style.color = 'var(--color-error)';
        }
        if (this._button) this._button.removeAttribute('disabled');
      });
  }
}

if (!customElements.get('dashboard-checkin-streak')) {
  customElements.define('dashboard-checkin-streak', DashboardCheckinStreak);
}
